import React, { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, Send, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

const REASON_CATEGORIES = [
  { id: 'irrelevant', label: 'Not relevant to my query' },
  { id: 'incomplete', label: 'Missing key information' },
  { id: 'incorrect', label: 'Contains wrong information' },
  { id: 'outdated', label: 'Outdated or stale content' },
  { id: 'other', label: 'Other' },
];

const QueryFeedback = ({ queryId }) => {
  const [selected, setSelected] = useState(null); // 'positive' | 'negative' | null
  const [showReasonForm, setShowReasonForm] = useState(false);
  const [reasonCategory, setReasonCategory] = useState(null);
  const [reasonText, setReasonText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Reset when query changes
  useEffect(() => {
    setSelected(null);
    setShowReasonForm(false);
    setReasonCategory(null);
    setReasonText('');
    setSubmitted(false);
  }, [queryId]);

  // Load existing feedback
  useEffect(() => {
    if (!queryId) return;
    const load = async () => {
      try {
        const res = await axiosInstance.get(`/queries/${queryId}/feedback`);
        if (res.data?.feedback_type) {
          setSelected(res.data.feedback_type);
          setSubmitted(true);
          if (res.data.reason_category) setReasonCategory(res.data.reason_category);
          if (res.data.reason) setReasonText(res.data.reason);
        }
      } catch { /* no existing feedback */ }
    };
    load();
  }, [queryId]);

  const submitFeedback = async (type, reason = null, category = null) => {
    setSubmitting(true);
    try {
      await axiosInstance.post(`/queries/${queryId}/feedback`, {
        feedback_type: type,
        reason: reason || null,
        reason_category: category || null,
      });
      setSelected(type);
      setSubmitted(true);
      setShowReasonForm(false);
      toast.success(type === 'positive' ? 'Thanks for the feedback!' : 'Feedback recorded — we\'ll work on improving.');
    } catch (err) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleThumbsUp = () => {
    if (selected === 'positive') return;
    submitFeedback('positive');
  };

  const handleThumbsDown = () => {
    if (selected === 'negative' && submitted) return;
    setSelected('negative');
    setShowReasonForm(true);
  };

  const handleReasonSubmit = () => {
    submitFeedback('negative', reasonText || null, reasonCategory);
  };

  const handleSkipReason = () => {
    submitFeedback('negative');
  };

  if (!queryId) return null;

  return (
    <div className="mt-4 pt-4 border-t border-gray-100" data-testid="query-feedback">
      {/* Feedback buttons row */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400 font-medium tracking-wide uppercase">
          Was this helpful?
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleThumbsUp}
            disabled={submitting}
            data-testid="feedback-thumbs-up"
            className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              selected === 'positive'
                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
          >
            <ThumbsUp size={14} className={selected === 'positive' ? 'fill-emerald-600' : 'group-hover:fill-emerald-200'} />
            {selected === 'positive' && <span className="text-xs">Helpful</span>}
          </button>
          <button
            onClick={handleThumbsDown}
            disabled={submitting}
            data-testid="feedback-thumbs-down"
            className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              selected === 'negative'
                ? 'bg-red-50 text-red-600 ring-1 ring-red-200'
                : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
            }`}
          >
            <ThumbsDown size={14} className={selected === 'negative' ? 'fill-red-400' : 'group-hover:fill-red-200'} />
            {selected === 'negative' && submitted && <span className="text-xs">Not helpful</span>}
          </button>
        </div>
      </div>

      {/* Negative feedback reason form */}
      {showReasonForm && !submitted && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 animate-in fade-in slide-in-from-top-1 duration-200" data-testid="feedback-reason-form">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-600">What went wrong? (optional)</span>
            <button onClick={handleSkipReason} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
              Skip <X size={12} />
            </button>
          </div>

          {/* Quick reason chips */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {REASON_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setReasonCategory(reasonCategory === cat.id ? null : cat.id)}
                data-testid={`feedback-reason-${cat.id}`}
                className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                  reasonCategory === cat.id
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Optional text */}
          <Textarea
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder="Tell us more (optional)..."
            className="text-sm min-h-[60px] bg-white"
            data-testid="feedback-reason-text"
          />

          <div className="flex justify-end mt-2">
            <Button
              size="sm"
              onClick={handleReasonSubmit}
              disabled={submitting}
              className="bg-gray-900 hover:bg-gray-800 text-white text-xs h-7 px-3"
              data-testid="feedback-submit-reason"
            >
              <Send size={12} className="mr-1" />
              {submitting ? 'Sending...' : 'Submit Feedback'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryFeedback;
